/** biome-ignore-all lint/a11y/noLabelWithoutControl: <explanation> */
"use client";
import { Combobox } from "@base-ui/react/combobox";
import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import type { City, Expertise, Project } from "@/types/consulting";
import ArrowIcon from "../icons/arrow";
import { Button } from "../ui/button";
import ExtendedImage from "../ui/extended-image";
import ExpertList from "./expert-list";

const ExpertiseImage = ({ title, image }: { title: string; image: string }) => {
  return (
    <div className="aspect-square size-56 border rounded-full relative">
      <ExtendedImage
        alt={title}
        className="w-auto h-auto rounded-full inset-0 object-cover"
        fill={true}
        src={image}
      />
    </div>
  );
};

const ExpertiseSelect = ({ project }: { project: Project }) => {
  const params = useSearchParams();
  const expertiseParam = params.get("expertise");
  const cityParam = params.get("city");
  const [expertise, setExpertise] = useState<Expertise | undefined>(() => {
    return project.expertise.find((item) => item.id === expertiseParam);
  });

  const [city, setCity] = useState<City | undefined>(undefined);

  const router = useRouter();
  const handleClick = () => {
    router.replace(`/${project.slug}?expertise=${expertise?.id}`);
  };
  const handleCitySelect = () => {
    router.replace(
      `/${project.slug}?expertise=${expertise?.id}&city=${city?.id}`,
    );
  };

  if (!expertiseParam && !cityParam) {
    return (
      <div className="flex flex-col justify-center items-center gap-4">
        <ExpertiseImage image={project.image} title={project.title} />
        <h2 className="text-3xl font-bold">Destek Köşesi</h2>
        <p className="text-lg leading-4.5 font-medium">{project.description}</p>
        <RadioGroup
          aria-labelledby={project.id}
          className="flex flex-col items-start gap-1.5 text-gray-900"
          onValueChange={setExpertise}
          value={expertise}
        >
          <div className="font-bold text-center w-full" id={project.id}>
            Uzmanlıklar
          </div>

          {project.expertise.map((expertise) => {
            return (
              <label
                className="flex items-center justify-between gap-2 font-normal border p-1 rounded-xl border-gray-300 w-full"
                key={expertise.id}
              >
                {expertise.label}
                <Radio.Root
                  className="flex size-5 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 data-[checked]:bg-gray-900 data-[unchecked]:border data-[unchecked]:border-gray-300"
                  value={expertise}
                >
                  <Radio.Indicator className="flex before:size-2 before:rounded-full before:bg-gray-50 data-unchecked:hidden" />
                </Radio.Root>
              </label>
            );
          })}
        </RadioGroup>
        <Button
          aria-disabled={!expertise}
          disabled={!expertise}
          onClick={handleClick}
        >
          Devam <ArrowIcon className="stroke-white" />
        </Button>
      </div>
    );
  }
  if (expertiseParam && !cityParam) {
    return (
      <div className="flex flex-col justify-center items-center gap-4">
        <ExpertiseImage image={project.image} title={project.title} />
        <p className="text-lg leading-4.5 font-medium">
          Almak istediğiniz danışmanın bulunduğı ili seçiniz
        </p>
        <div>
          <Combobox.Root
            items={
              project.expertise.find((item) => item.id === expertiseParam)
                ?.cities
            }
            onValueChange={(e) => setCity(e as City)}
            value={city}
          >
            <div className="relative flex flex-col gap-1 text-sm leading-5 font-bold text-gray-900">
              <Combobox.InputGroup className="relative box-content h-10 w-64 rounded-md border border-gray-200 bg-[canvas] focus-within:outline-2 focus-within:-outline-offset-1 focus-within:outline-blue-800 [&>input]:pr-[calc(0.5rem+1.5rem)] has-[.combobox-clear]:[&>input]:pr-[calc(0.5rem+1.5rem*2)]">
                <Combobox.Input
                  autoComplete="off"
                  className="h-full w-full border-0 bg-transparent pl-3.5 text-base font-normal text-gray-900 outline-none"
                  id="city"
                  placeholder="Şehir seçiniz"
                />
                <div className="absolute right-2 bottom-0 flex h-10 items-center justify-center text-gray-600">
                  <Combobox.Clear
                    aria-label="Clear selection"
                    className="combobox-clear flex h-10 w-6 items-center justify-center rounded bg-transparent p-0"
                  >
                    <XIcon className="size-4" />
                  </Combobox.Clear>
                  <Combobox.Trigger
                    aria-label="Open popup"
                    className="flex h-10 w-6 items-center justify-center rounded bg-transparent p-0"
                  >
                    <ChevronDownIcon className="size-4" />
                  </Combobox.Trigger>
                </div>
              </Combobox.InputGroup>
            </div>

            <Combobox.Portal>
              <Combobox.Positioner className="outline-none" sideOffset={4}>
                <Combobox.Popup className="w-[var(--anchor-width)] max-h-[23rem] max-w-[var(--available-width)] origin-[var(--transform-origin)] rounded-md bg-[canvas] text-gray-900 shadow-lg shadow-gray-200 outline-1 outline-gray-200 transition-[transform,scale,opacity] data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 dark:shadow-none dark:-outline-offset-1 dark:outline-gray-300 duration-100">
                  <Combobox.Empty>
                    <div className="p-4 text-[0.925rem] leading-4 text-gray-600">
                      Şehir bulunamadı.
                    </div>
                  </Combobox.Empty>
                  <Combobox.List className="outline-0 overflow-y-auto scroll-py-[0.5rem] py-2 overscroll-contain max-h-[min(23rem,var(--available-height))] data-[empty]:p-0">
                    {(item: City) => (
                      <Combobox.Item
                        className="grid cursor-default grid-cols-[0.75rem_1fr] items-center gap-2 py-2 pr-8 pl-4 text-base leading-4 outline-none select-none data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-gray-50 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-2 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-gray-900"
                        key={item.id}
                        value={item}
                      >
                        <Combobox.ItemIndicator className="col-start-1">
                          <CheckIcon className="size-3" />
                        </Combobox.ItemIndicator>
                        <span className="col-start-2">{item.label}</span>
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Root>
        </div>
        <Button
          aria-disabled={!expertise}
          disabled={!expertise}
          onClick={handleCitySelect}
        >
          Devam <ArrowIcon className="stroke-white" />
        </Button>
      </div>
    );
  }

  return (
    <ExpertList consultingId={project.id} expertises={project.expertise} />
  );
};

export default ExpertiseSelect;
